import {Sprite, Text} from 'pixi.js';
import {
    Button,
    Checkbox,
    CommandLineInterface,
    CoreDI,
    DeviceType,
    inject,
    LanguageService,
    setLayer
} from '@gongxigames/framework/core';
import {
    GlobalEventsMapForBackwardCompatibility,
    UIElementRepositionData,
    UIElements,
    UIElementsSwapLayerEvent
} from '@gongxigames/framework/slot/events';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {ILogic} from '@gongxigames/framework/slot/types';
import {IKeypadControlsView} from '@gongxigames/framework/slot/modules/keypadControls/IKeypadControlsView';
import {KeypadControlsViewEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewEvents';
import {KeypadView} from '@gongxigames/framework/slot/modules/keypad';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export class KeypadControlsView extends View<GlobalEventsMapForBackwardCompatibility> implements IKeypadControlsView {
    public static readonly NAME: symbol = Symbol('KeypadControlsView');
    public static layer = LAYERS.KEYPAD;
    public static platform: DeviceType = DeviceType.DESKTOP;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.CLI)
    protected _cli!: CommandLineInterface;

    protected _infoButton!: Button;
    protected _autoplayButton!: Button;
    protected _betDecButton!: Button;
    protected _betIncButton!: Button;
    protected _betLabel!: Text;
    protected _betValueText!: Text;
    protected _turboCheckbox!: Checkbox;
    protected _totalBetPanel!: Sprite;
    protected _disabledTurboCheckbox = false;
    protected _disabledAutoplayButton = false;

    public init(): void {
        super.init();

        this.initGraphicElements();
        this.initCallbacks();
    }

    protected initGraphicElements(): void {
        this._totalBetPanel = this.mainSprite.getChildByName<Sprite>('totalBet');

        this._infoButton = this.mainSprite.getChildByName<Button>('infoButton');
        this._autoplayButton = this.mainSprite.getChildByName<Button>('autoButton');
        this._betIncButton = this._totalBetPanel.getChildByName<Button>('plusButton');
        this._betDecButton = this._totalBetPanel.getChildByName<Button>('minusButton');
        this.betLabel = this._totalBetPanel.getChildByName<Text>('label');
        this.betValueText = this._totalBetPanel.getChildByName<Text>('fieldText');

        this.setupTurboCheckbox();
    }

    protected setupTurboCheckbox(): void {
        this.setTurboCheckbox(this.mainSprite.getChildByName<Button>('turboButtonOn'),
            this.mainSprite.getChildByName<Button>('turboButtonOff'));
    }

    public hideBetPanel(): void {
        this.betLabel.visible = false;
        this.betValueText.visible = false;

    }

    public showBetPanel(): void {
        this.betLabel.visible = true;
        this.betValueText.visible = true;
    }

    public disableForeverAutoplayButton(): void {
        this._autoplayButton.disable();
        this._autoplayButton.visible = false;
        this._disabledAutoplayButton = true;
    }

    public disableForeverTurboCheckbox(): void {
        this._turboCheckbox.disable();
        this._turboCheckbox.hide();
        this._disabledTurboCheckbox = true;
    }

    public enableInfoButton(): void {
        this._infoButton.enable();
    }

    public enableTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._turboCheckbox.enable();
    }

    public enableBetButtons(): void {
        this._betDecButton.enable();
        this._betIncButton.enable();
    }

    public disableInfoButton(): void {
        this._infoButton.disable();
    }

    public disableTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._turboCheckbox.disable();
    }

    public disableBetButtons(): void {
        this._betDecButton.disable();
        this._betIncButton.disable();
    }

    public playTransitionToFreeSpins(): void {
        this.hideAutoplayButton();
        this.hideTurboCheckbox();
    }

    public playTransitionToMainGame(): void {
        this.showAutoplayButton();
        this.showTurboCheckbox();
    }

    public enableAutoplayButton(): void {
        if (this._disabledAutoplayButton) {
            return;
        }

        this._autoplayButton.enable();
    }

    public disableAutoplayButton(): void {
        if (this._disabledAutoplayButton) {
            return;
        }

        this._autoplayButton.disable();
    }

    protected showAutoplayButton(): void {
        if (this._disabledAutoplayButton) {
            return;
        }

        this._autoplayButton.visible = true;
    }

    protected hideAutoplayButton(): void {
        if (this._disabledAutoplayButton) {
            return;
        }

        this._autoplayButton.visible = false;
    }

    protected showTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._turboCheckbox.show();
    }

    protected hideTurboCheckbox(): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._turboCheckbox.hide();
    }

    public enableBetIncButton(): void {
        this._betIncButton.enable();
    }

    public disableBetIncButton(): void {
        this._betIncButton.disable();
    }

    public enableBetDecButton(): void {
        this._betDecButton.enable();
    }

    public disableBetDecButton(): void {
        this._betDecButton.disable();
    }

    public enableMenuButton(): void {
    }

    public disableMenuButton(): void {
    }

    public enableHomeButton(): void {
    }

    public disableHomeButton(): void {
    }

    public enableTotalBetOpenButton(): void {
    }

    public disableTotalBetOpenButton(): void {
    }

    public showTotalBetOpenButton(): void {
    }

    public hideTotalBetOpenButton(): void {
    }

    public setBetValueText(text: string): void {
        this._betValueText.text = text;
    }

    public openTotalBetWindow(): void {
        // functionality does not exist for desktop
    }

    public closeTotalBetWindow(): void {
        // functionality does not exist for desktop
    }

    public updateTurboCheckbox(isOn: boolean): void {
        if (this._disabledTurboCheckbox) {
            return;
        }

        this._turboCheckbox.setValue(isOn);
    }

    public setTurboCheckbox(onButton: Button, offButton: Button): void {
        this._turboCheckbox = new Checkbox(onButton, offButton);
    }

    public swapUIElementsLayer({layer, element}: UIElementsSwapLayerEvent): void {
        if (element === UIElements.TOTAL_BET_PANEL) {
            setLayer(layer, this._totalBetPanel);
        }
    }

    public repositionElements(data: UIElementRepositionData[]): void {}

    protected initCallbacks(): void {
        this._infoButton.callback = this.onInfoButtonClicked.bind(this);
        this._autoplayButton.callback = this.onAutoplayButtonClicked.bind(this);
        this._betDecButton.callback = this.onDecBetButtonClicked.bind(this);
        this._betIncButton.callback = this.onIncBetButtonClicked.bind(this);
        this._turboCheckbox.changedCallback = this.onTurboCheckboxClicked.bind(this);

        /* develblock:start */
        this._cli.addCommand('disableTurbo', () => this._turboCheckbox.setValue(false, true));
        this._cli.addCommand('enableTurbo', () => this._turboCheckbox.setValue(true, true));
        /* develblock:end */
    }

    protected onInfoButtonClicked(): void {
        this.soundPlayer.play('buttonclick');
        this.emit(KeypadControlsViewEvents.INFO_CLICKED);
    }

    protected onAutoplayButtonClicked(): void {
        this.soundPlayer.play('spinclick');
        this.emit(KeypadControlsViewEvents.AUTOPLAY_CLICKED);
    }

    protected onDecBetButtonClicked(): void {
        this.emit(KeypadControlsViewEvents.BET_DEC_CLICKED);
    }

    protected onIncBetButtonClicked(): void {
        this.emit(KeypadControlsViewEvents.BET_INC_CLICKED);
    }

    protected onTurboCheckboxClicked(isOn: boolean): void {
        this.soundPlayer.play('buttontoggle');
        this.emit(KeypadControlsViewEvents.TURBO_CHECKBOX_CLICKED, isOn);
    }

    protected set betValueText(value: Text) {
        this._betValueText = value;

        if (value) {
            this._betValueText.visible = this.getSettings<SlotSettings>().get('showBet');
        }
    }

    protected set betLabel(value: Text) {
        this._betLabel = value;
        value.text = this._languageService.get('totalBetLabel');
    }

    protected get betLabel(): Text {
        return this._betLabel;
    }

    protected get betValueText(): Text {
        return this._betValueText;
    }

    protected get mainSprite(): Sprite {
        return this._installer.getComponent<KeypadView>(KeypadView.NAME).mainSprite;
    }
}
